/**
 * Beat 12, *Dawn Transport* — `docs/beat-12-plan.md`, stages 1–4.
 *
 * What exists: she comes out of the cull door into the dawn yard; `the-plan`
 * asks for marks 1 and 2 in either order (the junction box; Holly holding the
 * race gate); then `the-herd` (mark 3): the herd follows her out of the race
 * and across the yard, and the beat closes when its head reaches the vehicle
 * gate or the driver brings the floods back.
 *
 * **Every stage ends on a clock as well as a condition** (the plan's authored
 * sequence): a player who presses nothing reaches `end`, and no backstop
 * punishes — it only completes.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

export const DAWN_TRANSPORT_FLAGS = {
  /** Two metres clear of the cull door. Raised by the beat module, off the room's spawn. */
  clearOfDoor: 'dawn.clearOfDoor',
  /** Mark 1: the floods and the shed lamp are out and the ramp is jammed. Raised by the ROOM. */
  lightsOut: 'dawn.lightsOut',
  /** Mark 2: Holly has swung the race gate onto the yard and holds it. Raised by the ROOM (stage 4). */
  gateHeld: 'dawn.gateHeld',
  /** Mark 3: the head of the herd has reached the vehicle gate. Raised by the ROOM (stage 5). */
  herdAtGate: 'dawn.herdAtGate',
  /** The driver has finished at the box and the floods are back. Raised by the ROOM (stage 3). */
  floodsBack: 'dawn.floodsBack',
} as const;

export const DAWN_TRANSPORT_CUES = {
  open: 'dawn.open',
  /** The close's held shot begins: her in the gateway. */
  hold: 'dawn.hold',
  fadeOut: 'dawn.fadeOut',
  /**
   * `the-plan`'s backstop, under black: the room applies every mark not yet
   * done, silently. It completes; it never punishes (spec §5.6).
   */
  applyPlan: 'dawn.applyPlan',
} as const;

/** The plan's `dawn` stage: 2 m clear of the door, or 20 s. */
export const DAWN_PATIENCE = 20;

/**
 * The plan's backstop (the plan's authored sequence, `PLAN_PATIENCE`). Four
 * minutes: the plan is the beat's thinking, and a player reading the yard and
 * her map is playing it.
 */
export const PLAN_PATIENCE = 240;

/** The backstop's black: fade out, apply at the middle of it, fade back in. */
export const PLAN_BACKSTOP_BLACK = 1.6;

/**
 * `the-herd`'s backstop (the plan's `HERD_PATIENCE`). The driver's clock
 * normally ends the stage first — ~41 s from mark 1 — so this only closes a
 * run where she did mark 2 last and then stood still.
 */
export const HERD_PATIENCE = 120;

/** The plan's close: a 5 s hold on her in the gateway, then the fade. */
export const GATE_HOLD = 5;

/** Black held after the fade starts, before `end` hands over. Beat 11's value. */
const CLOSE_HOLD = 2.4;

function dawn(): Stage {
  return {
    id: 'dawn',
    enter: (_ctx, emit): void => emit(DAWN_TRANSPORT_CUES.open),
    done: (ctx: StoryContext): boolean => ctx.completed.has(DAWN_TRANSPORT_FLAGS.clearOfDoor) || ctx.stageTime >= DAWN_PATIENCE,
  };
}

/** The emit for a clock crossing `at` on this step: stateless, so a `Beat` stays shareable. */
function crossed(ctx: StoryContext, dt: number, at: number): boolean {
  return ctx.stageTime >= at && ctx.stageTime - dt < at;
}

/** Marks 1 and 2, in either order (the plan: *"any order is true for 1 and 2"*). */
function planDone(ctx: StoryContext): boolean {
  return ctx.completed.has(DAWN_TRANSPORT_FLAGS.lightsOut) && ctx.completed.has(DAWN_TRANSPORT_FLAGS.gateHeld);
}

function thePlan(): Stage {
  return {
    id: 'the-plan',
    update: (dt, ctx, emit): void => {
      if (planDone(ctx)) return;
      if (crossed(ctx, dt, PLAN_PATIENCE)) emit(DAWN_TRANSPORT_CUES.fadeOut);
      if (crossed(ctx, dt, PLAN_PATIENCE + PLAN_BACKSTOP_BLACK / 2)) emit(DAWN_TRANSPORT_CUES.applyPlan);
    },
    done: (ctx: StoryContext): boolean => planDone(ctx) || ctx.stageTime >= PLAN_PATIENCE + PLAN_BACKSTOP_BLACK,
    // Only the backstop went to black, so only it comes back from it.
    exit: (ctx, emit): void => {
      if (ctx.stageTime >= PLAN_PATIENCE) emit(DAWN_TRANSPORT_CUES.open);
    },
  };
}

function theHerd(): Stage {
  return {
    id: 'the-herd',
    // The floods coming back is a close too (the plan: *"the head reaches the
    // vehicle gate, or the floods come back"*) — a fade on a lit yard with
    // the herd still in it, not a loss. Beat 13 runs from either.
    done: (ctx: StoryContext): boolean => ctx.completed.has(DAWN_TRANSPORT_FLAGS.herdAtGate)
      || ctx.completed.has(DAWN_TRANSPORT_FLAGS.floodsBack)
      || ctx.stageTime >= HERD_PATIENCE,
  };
}

/**
 * The plan's `the-gate` is a hold and then a fade; it is two stages here so
 * neither needs a latch, and a `Beat` stays a constant any number of
 * sequencers can share.
 */
export const DAWN_TRANSPORT_BEAT: Beat = {
  number: 12,
  id: 'act3.dawnTransport',
  stages: [
    dawn(),
    thePlan(),
    theHerd(),
    {
      id: 'the-gate',
      enter: (_ctx, emit): void => emit(DAWN_TRANSPORT_CUES.hold),
      done: (ctx: StoryContext): boolean => ctx.stageTime >= GATE_HOLD,
    },
    {
      id: 'close',
      enter: (_ctx, emit): void => emit(DAWN_TRANSPORT_CUES.fadeOut),
      done: (ctx: StoryContext): boolean => ctx.stageTime >= CLOSE_HOLD,
    },
  ],
};
